import { supabase } from '../supabaseClient';

export const clientDocumentsService = {
    BUCKET_NAME: 'client-documents',

    async getByClientId(clientId) {
        const { data, error } = await supabase
            .from('client_documents')
            .select('*')
            .eq('client_id', clientId)
            .order('uploaded_at', { ascending: false });
        if (error) throw error;
        return data || [];
    },

    async uploadFile(clientId, agentNpn, file, description = '') {
        // Validar tamaño (10MB máximo)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            throw new Error('El archivo es demasiado grande. Máximo 10MB.');
        }

        // Validar tipo (PDF o imágenes)
        const allowedTypes = [
            'application/pdf',
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/gif',
            'image/webp'
        ];
        if (!allowedTypes.includes(file.type)) {
            throw new Error('Tipo de archivo no permitido. Solo PDF e imágenes.');
        }

        // Crear path único: client-{clientId}/{timestamp}-{filename}
        const timestamp = Date.now();
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const filePath = `${clientId}/${timestamp}-${sanitizedFileName}`;

        // Subir archivo a Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
            .from(this.BUCKET_NAME)
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false
            });

        if (uploadError) {
            throw new Error(`Error al subir archivo: ${uploadError.message}`);
        }

        // Obtener URL pública
        const { data: urlData } = supabase.storage
            .from(this.BUCKET_NAME)
            .getPublicUrl(filePath);

        // Guardar metadata en la base de datos
        const documentData = {
            client_id: clientId,
            agent_npn: agentNpn,
            file_name: file.name,
            file_path: filePath,
            file_size: file.size,
            file_type: file.type,
            description: description || null
        };

        const { data, error: dbError } = await supabase
            .from('client_documents')
            .insert([documentData])
            .select()
            .single();

        if (dbError) {
            // Si falla la DB, intentar eliminar el archivo de Storage
            await supabase.storage
                .from(this.BUCKET_NAME)
                .remove([filePath]);
            throw new Error(`Error al guardar metadata: ${dbError.message}`);
        }

        return { ...data, public_url: urlData.publicUrl };
    },

    async deleteFile(documentId) {
        // Obtener metadata del documento
        const { data: document, error: fetchError } = await supabase
            .from('client_documents')
            .select('file_path')
            .eq('id', documentId)
            .single();

        if (fetchError) throw fetchError;

        // Eliminar de Storage
        const { error: storageError } = await supabase.storage
            .from(this.BUCKET_NAME)
            .remove([document.file_path]);

        if (storageError) {
            // Continuar incluso si falla Storage para eliminar la metadata
        }

        // Eliminar metadata de la base de datos
        const { error: dbError } = await supabase
            .from('client_documents')
            .delete()
            .eq('id', documentId);

        if (dbError) throw dbError;
    },

    async update(id, updates) {
        const { data, error } = await supabase
            .from('client_documents')
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    getPublicUrl(filePath) {
        const { data } = supabase.storage
            .from(this.BUCKET_NAME)
            .getPublicUrl(filePath);
        return data.publicUrl;
    }
};
